import React, { useState, useEffect, useCallback } from 'react';

/**
 * Alerts tab for the Care dashboard. Lists crisis events for one managed
 * account (resolved + unresolved) and lets the caregiver mark unresolved
 * ones resolved. Uses GET /api/managed-accounts/{id}/crisis-events and
 * POST .../crisis-events/{eventId}/resolve.
 */
export default function CareAlertsTab({ account, token, api, onChanged }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolvingId, setResolvingId] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${api}/api/managed-accounts/${encodeURIComponent(account.id)}/crisis-events`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setEvents(data.crisis_events || []);
    } catch (e) {
      setError(e.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [api, token, account.id]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const resolve = async (ev) => {
    setResolvingId(ev.id);
    try {
      const res = await fetch(
        `${api}/api/managed-accounts/${encodeURIComponent(account.id)}/crisis-events/${ev.id}/resolve`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({}) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchEvents();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div>
      {error && <div className="care-error-state" role="alert">{error}</div>}
      {loading ? (
        <><div className="care-skeleton" /><div className="care-skeleton" /></>
      ) : events.length === 0 ? (
        <div className="care-empty-state">No alerts. Nothing concerning has been detected on calls.</div>
      ) : (
        events.map((ev) => {
          const resolved = !!ev.resolved_at;
          return (
            <div key={ev.id} className="care-list-row" style={resolved ? { opacity: 0.6 } : { borderColor: 'rgba(248,113,113,0.35)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="care-row-title">
                  {resolved ? '✓ ' : '⚠️ '}
                  Detected: <span style={{ color: resolved ? 'inherit' : '#f87171' }}>{ev.trigger_phrase}</span>
                </div>
                <div className="care-row-meta">
                  {ev.triggered_at ? new Date(ev.triggered_at).toLocaleString() : ''}
                  {resolved ? ' · resolved' : ''}
                </div>
                <div className="care-row-body">"{ev.transcript_excerpt || '(no transcript captured)'}"</div>
              </div>
              {!resolved && (
                <div className="care-row-actions">
                  <button className="care-mini-btn" onClick={() => resolve(ev)} disabled={resolvingId === ev.id} aria-label="Mark alert resolved">
                    {resolvingId === ev.id ? 'Resolving…' : 'Mark resolved'}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
      <div style={{ fontSize: 11, color: 'rgba(254,252,246,0.45)', marginTop: 12 }}>
        Alerts are keyword-based and may be false alarms. If you're ever unsure, check on {account.name} directly.
      </div>
    </div>
  );
}
